import { useState, useCallback, useMemo, useEffect } from 'react';
import { DateRange, Property, PresetOption } from '../types';
import { useFinancialDataCache } from './useFinancialDataCache';

export interface FinancialDashboardState {
  selectedProperties: string[];
  dateRange: DateRange;
  loading: boolean;
  error: string | null;
}

export interface FinancialDashboardActions {
  setSelectedProperties: (propertyIds: string[]) => void;
  setDateRange: (range: DateRange) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  resetFilters: () => void;
}

export interface UseFinancialDashboardStateOptions {
  defaultProperties?: string[];
  defaultDateRange?: DateRange;
}

// Default date range - current month
const getDefaultDateRange = (): DateRange => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  
  return {
    startDate: startOfMonth,
    endDate: endOfMonth,
    preset: 'this_month',
  };
};

// Preset date range options
export const getDateRangePresets = (): PresetOption[] => {
  const now = new Date();
  
  return [
    {
      label: 'This month',
      value: 'this_month',
      startDate: new Date(now.getFullYear(), now.getMonth(), 1),
      endDate: new Date(now.getFullYear(), now.getMonth() + 1, 0),
    },
    {
      label: 'Last 30 days',
      value: 'last_30_days',
      startDate: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000),
      endDate: now,
    },
    {
      label: 'Last month',
      value: 'last_month',
      startDate: new Date(now.getFullYear(), now.getMonth() - 1, 1),
      endDate: new Date(now.getFullYear(), now.getMonth(), 0),
    },
    {
      label: 'Last 3 months',
      value: 'last_3_months',
      startDate: new Date(now.getFullYear(), now.getMonth() - 3, 1),
      endDate: now,
    },
    {
      label: 'Last 6 months',
      value: 'last_6_months',
      startDate: new Date(now.getFullYear(), now.getMonth() - 6, 1),
      endDate: now,
    },
    {
      label: 'Year to date',
      value: 'year_to_date',
      startDate: new Date(now.getFullYear(), 0, 1),
      endDate: now,
    },
    {
      label: 'Last year',
      value: 'last_year',
      startDate: new Date(now.getFullYear() - 1, 0, 1),
      endDate: new Date(now.getFullYear() - 1, 11, 31),
    },
  ];
};

export const useFinancialDashboardState = (
  options: UseFinancialDashboardStateOptions = {}
) => {
  const {
    defaultProperties = [],
    defaultDateRange = getDefaultDateRange(),
  } = options;

  const { loadDashboardPreferences, saveDashboardPreferences } = useFinancialDataCache();

  // Load cached preferences on mount (only for properties, not date range)
  const [initialPreferences] = useState(() => {
    const cached = loadDashboardPreferences();
    
    // Always use default date range, but cache properties if available
    return {
      selectedProperties: cached?.selectedProperties || defaultProperties,
      dateRange: {
        startDate: defaultDateRange.startDate.toISOString(),
        endDate: defaultDateRange.endDate.toISOString(),
        preset: defaultDateRange.preset,
      },
    };
  });

  // State with cached initial values for properties, default for date range
  const [selectedProperties, setSelectedProperties] = useState<string[]>(
    initialPreferences.selectedProperties
  );
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    // Debug log (remove in production)
    if (process.env.NODE_ENV === 'development') {
      console.log('Dashboard State: Initializing with default date range', {
        preset: defaultDateRange.preset,
        startDate: defaultDateRange.startDate.toISOString().split('T')[0],
        endDate: defaultDateRange.endDate.toISOString().split('T')[0]
      });
    }
    return defaultDateRange;
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Actions with caching
  const actions: FinancialDashboardActions = useMemo(() => ({
    setSelectedProperties: (propertyIds: string[]) => {
      setSelectedProperties(propertyIds);
      setError(null); // Clear error when filters change
      
      // Cache preferences
      saveDashboardPreferences({
        selectedProperties: propertyIds,
      });
    },
    
    setDateRange: (range: DateRange) => {
      console.log('Dashboard State: Setting date range', {
        preset: range.preset,
        startDate: range.startDate.toISOString().split('T')[0],
        endDate: range.endDate.toISOString().split('T')[0]
      });
      
      setDateRange(range);
      setError(null); // Clear error when filters change
      
      // Cache preferences
      saveDashboardPreferences({
        dateRange: {
          startDate: range.startDate.toISOString(),
          endDate: range.endDate.toISOString(),
          preset: range.preset,
        },
      });
    },
    
    setLoading,
    
    setError,
    
    clearError: () => setError(null),
    
    resetFilters: () => {
      setSelectedProperties(defaultProperties);
      setDateRange(defaultDateRange);
      setError(null);
      
      // Clear cached preferences
      saveDashboardPreferences({
        selectedProperties: defaultProperties,
        dateRange: {
          startDate: defaultDateRange.startDate.toISOString(),
          endDate: defaultDateRange.endDate.toISOString(),
          preset: defaultDateRange.preset,
        },
      });
    },
  }), [defaultProperties, defaultDateRange, saveDashboardPreferences]);

  // Computed values
  const hasFilters = useMemo(() => {
    return selectedProperties.length > 0;
  }, [selectedProperties]);

  const isValidDateRange = useMemo(() => {
    return dateRange.startDate <= dateRange.endDate;
  }, [dateRange]);

  const canLoadData = useMemo(() => {
    return hasFilters && isValidDateRange && !loading;
  }, [hasFilters, isValidDateRange, loading]);

  // Date range presets
  const presetOptions = useMemo(() => getDateRangePresets(), []);

  // Helper functions
  const getDateRangeLabel = useCallback(() => {
    if (dateRange.preset) {
      const preset = presetOptions.find(p => p.value === dateRange.preset);
      if (preset) return preset.label;
    }
    
    const startStr = dateRange.startDate.toLocaleDateString();
    const endStr = dateRange.endDate.toLocaleDateString();
    return `${startStr} - ${endStr}`;
  }, [dateRange, presetOptions]);

  const getSelectedPropertiesLabel = useCallback((properties: Property[]) => {
    if (selectedProperties.length === 0) return 'No properties selected';
    if (selectedProperties.length === 1) {
      const property = properties.find(p => p.id === selectedProperties[0]);
      return property?.name || 'Unknown property';
    }
    if (selectedProperties.length === properties.length) return 'All properties';
    return `${selectedProperties.length} properties selected`;
  }, [selectedProperties]);

  // State object
  const state: FinancialDashboardState = {
    selectedProperties,
    dateRange,
    loading,
    error,
  };

  return {
    state,
    actions,
    computed: {
      hasFilters,
      isValidDateRange,
      canLoadData,
      presetOptions,
    },
    helpers: {
      getDateRangeLabel,
      getSelectedPropertiesLabel,
    },
  };
};